import { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';

const API_BASE = 'https://api.finance360.cr/api/v1';

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="relative group">
      <button onClick={handleCopy} className="absolute top-2 right-2 p-1 rounded bg-gray-700 hover:bg-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
        {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-gray-300" />}
      </button>
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto"><code>{code}</code></pre>
    </div>
  );
}

function Endpoint({ method, path, description, children }) {
  const [open, setOpen] = useState(false);
  const colors = { GET: 'bg-green-100 text-green-700 border-green-300', POST: 'bg-blue-100 text-blue-700 border-blue-300', PUT: 'bg-amber-100 text-amber-700 border-amber-300', DELETE: 'bg-red-100 text-red-700 border-red-300' };
  return (
    <div className="border rounded-lg overflow-hidden mb-2">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left">
        {open ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />}
        <span className={`px-2 py-0.5 rounded text-xs font-bold border ${colors[method]}`}>{method}</span>
        <code className="text-sm font-mono text-gray-800">{path}</code>
        <span className="text-sm text-gray-500 ml-auto">{description}</span>
      </button>
      {open && <div className="px-4 pb-4 border-t bg-gray-50 space-y-3 pt-3">{children}</div>}
    </div>
  );
}

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left font-bold text-gray-900">
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        {title}
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

function Step({ number, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">{number}</div>
      <div className="flex-1 pb-6 border-l-2 border-blue-200 pl-6 -ml-4 ml-0">
        <h4 className="font-bold text-gray-900 mb-2">{title}</h4>
        {children}
      </div>
    </div>
  );
}

export default function ApiDocumentation() {
  return (
    <div className="space-y-4 max-w-5xl">
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h2 className="text-lg font-bold text-blue-900 mb-1">API REST — Finance 360</h2>
        <p className="text-sm text-blue-800 mb-2">
          Permite a sistemas externos (CRM, ERP, eCommerce) generar Facturas Electrónicas, Notas de Crédito, gestionar clientes y productos.
        </p>
        <code className="text-xs bg-blue-100 px-2 py-1 rounded font-mono">{API_BASE}</code>
      </div>

      {/* 1. Autenticación */}
      <Section title="1. Autenticación" defaultOpen={true}>
        <p className="text-sm text-gray-600 mb-3">
          Todas las peticiones llevan el header <code className="bg-gray-100 px-1 rounded">x-api-key</code> y el header
          <code className="bg-gray-100 px-1 rounded ml-1">x-signature</code> (HMAC-SHA256 del cuerpo de la petición, en hexadecimal,
          firmado con el API Secret; para GET se firma una cadena vacía). Opcionalmente <code className="bg-gray-100 px-1 rounded">x-timestamp</code>
          en milisegundos, con una tolerancia de 5 minutos. Se obtienen al crear una credencial en <strong>Configuración → Credenciales API</strong>.
        </p>
        <CodeBlock code={`# Ejemplo con curl
curl -X GET "${API_BASE}/status" \\
  -H "x-api-key: k360_abc123..." \\
  -H "x-signature: <hmac_sha256_hex(cuerpo, api_secret)>" \\
  -H "x-timestamp: 1759330000000" \\
  -H "Content-Type: application/json"

# Respuesta
{ "success": true, "status": "ok" }`} />
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
          <strong>El API Secret solo se muestra UNA VEZ al crear la credencial.</strong> Guárdelo en un lugar seguro.
        </div>

        <h4 className="text-sm font-semibold text-gray-800 mt-4 mb-1">Empresa sobre la que se opera</h4>
        <p className="text-sm text-gray-600 mb-2">
          Una credencial creada para <strong>una empresa</strong> trabaja siempre sobre esa empresa; no hace falta indicarla.
          Una credencial con alcance <strong>Todas las empresas de la cuenta</strong> debe indicar en cada llamada la empresa con el header
          <code className="bg-gray-100 px-1 rounded ml-1">X-Emisor-ID</code> (o el parámetro <code className="bg-gray-100 px-1 rounded">emisor_id</code>).
          El listado de empresas con sus ids se obtiene en <code className="bg-gray-100 px-1 rounded">GET {API_BASE}/emisores</code>.
        </p>
        <CodeBlock code={`# Credencial de toda la cuenta: facturar a nombre de la empresa 3
curl -X POST "${API_BASE}/documents" \\
  -H "x-api-key: k360_abc123..." \\
  -H "x-signature: <hmac_sha256_hex(cuerpo, api_secret)>" \\
  -H "X-Emisor-ID: 3" \\
  -H "Content-Type: application/json" \\
  -d '{ ... }'

# Sin X-Emisor-ID en una credencial de toda la cuenta:
{ "success": false, "error": "Company context is required. Please provide X-Emisor-ID header or emisor_id query parameter" }`} />
      </Section>

      {/* 2. Flujo Completo */}
      <Section title="2. Flujo Completo: Crear una Factura Electrónica" defaultOpen={true}>
        <p className="text-sm text-gray-600 mb-4">Siga estos pasos en orden para emitir una FE desde su sistema.</p>

        <Step number="1" title="Buscar o crear el cliente">
          <p className="text-sm text-gray-600 mb-2">Busque si el cliente ya existe por su cédula. Si no existe, créelo.</p>
          <CodeBlock code={`# Buscar por cédula
GET ${API_BASE}/receptores/buscar/3101999888

# Si no existe (404), crear:
POST ${API_BASE}/receptores
{
  "tipoIdentificacion": "02",
  "identificacion": "3101999888",
  "nombre": "EJEMPLO COMERCIAL S.A.",
  "email": "info@ejemplo.cr",
  "emailFacturacion": "facturas@ejemplo.cr",
  "telefonoCodigo": "506",
  "telefonoNumero": "22001234",
  "condicionVentaDefecto": "02",
  "monedaDefecto": "USD",
  "diasCredito": 30
}

# Respuesta: { "success": true, "data": { "id": 1, ... } }
# Guarde el "id" para usarlo en la factura`} />
        </Step>

        <Step number="2" title="(Opcional) Registrar exoneración del cliente">
          <p className="text-sm text-gray-600 mb-2">Si el cliente tiene exoneración de IVA, regístrela con el número de autorización. El sistema consulta Hacienda automáticamente.</p>
          <CodeBlock code={`POST ${API_BASE}/receptores/1/exoneraciones
{
  "numeroDocumento": "AL-00001234-25"
}

# El sistema consulta Hacienda y registra:
# - CABYS autorizados
# - Porcentaje de exoneración
# - Fechas de vigencia
# - Institución`} />
        </Step>

        <Step number="3" title="(Opcional) Buscar código CABYS del producto">
          <p className="text-sm text-gray-600 mb-2">Si no conoce el código CABYS, búsquelo por nombre.</p>
          <CodeBlock code={`GET ${API_BASE}/cabys/search?q=internet

# Respuesta:
{
  "data": [
    { "codigo": "8422200000000", "descripcion": "Servicios de telecomunicaciones por Internet", "impuesto": 13 }
  ]
}`} />
        </Step>

        <Step number="4" title="Crear la factura">
          <p className="text-sm text-gray-600 mb-2">Envíe la factura con los datos del cliente, líneas y condiciones de venta.</p>
          <CodeBlock code={`POST ${API_BASE}/documents
{
  "tipoDocumento": "01",
  "receptor": { "id": 1 },
  "condicionVenta": "02",
  "medioPago": ["04"],
  "codigoMoneda": "USD",
  "submit": true,
  "notas": "Periodo de cobro: Mayo 2026",
  "lineas": [
    {
      "codigo": "8422200000000",
      "codigoComercial": "SRV-001",
      "detalle": "Servicio de Internet 100Mbps\\nMes de Abril 2026",
      "cantidad": 1,
      "unidadMedida": "Sp",
      "precioUnitario": 50.00,
      "impuesto": {
        "codigo": "01",
        "codigoTarifa": "08",
        "tarifa": 13
      }
    }
  ]
}

# submit: true  → Envía directo a Hacienda
# submit: false → Queda en Facturación API para revisión
# Si "Envío directo" está activado, submit=true es el default
# notas: Comentarios/observaciones que aparecen en el PDF de la factura

# Respuesta (submit: true):
{
  "success": true,
  "data": {
    "id": 100,
    "consecutivo": "00100001010000000001",
    "clave": "50601011900012345678...",
    "estadoHacienda": "procesando",
    "totalComprobante": 56.50
  },
  "reviewRequired": false
}

# Respuesta (submit: false):
{
  "success": true,
  "data": {
    "id": 100,
    "consecutivo": null,
    "estadoHacienda": "borrador",
    "totalComprobante": 56.50,
    "apiReviewStatus": "pending_review"
  },
  "reviewRequired": true
}`} />
          <div className="p-2 bg-indigo-50 border border-indigo-200 rounded text-xs text-indigo-800">
            <strong>Control de envío:</strong> Si "Envío directo" está desactivado, el sistema externo decide por request: enviar <code className="mx-1">"submit": true</code> para enviar directo a Hacienda, o no enviarlo / <code className="mx-1">"submit": false</code> para que quede pendiente en "Facturación API" con <code className="mx-1">"reviewRequired": true</code>.
          </div>
        </Step>

        <Step number="5" title="Consultar estado en Hacienda (polling)">
          <p className="text-sm text-gray-600 mb-2">Espere 5-10 segundos y consulte el estado. Repita hasta obtener "aceptado" o "rechazado".</p>
          <CodeBlock code={`GET ${API_BASE}/documents/100/status

# Respuesta:
{
  "data": {
    "id": 100,
    "estadoHacienda": "aceptado",
    "apiReviewStatus": null,
    "mensajeHacienda": "Aceptado"
  }
}`} />
          <div className="p-2 bg-gray-100 rounded text-xs">
            <strong>Estados posibles:</strong> borrador → procesando → <span className="text-green-700 font-bold">aceptado</span> | <span className="text-red-700 font-bold">rechazado</span> | error
          </div>
        </Step>

        <Step number="6" title="Descargar PDF o XML">
          <CodeBlock code={`# PDF de la factura
GET ${API_BASE}/documents/100/pdf
# Content-Type: application/pdf

# XML firmado
GET ${API_BASE}/documents/100/xml
# Content-Type: application/xml`} />
        </Step>
      </Section>

      {/* 3. Nota de Crédito */}
      <Section title="3. Crear una Nota de Crédito (NC)">
        <p className="text-sm text-gray-600 mb-3">Para anular o corregir una FE, cree una NC referenciando la factura original.</p>
        <CodeBlock code={`POST ${API_BASE}/documents
{
  "tipoDocumento": "03",
  "receptor": { "id": 1 },
  "condicionVenta": "02",
  "medioPago": ["04"],
  "codigoMoneda": "USD",
  "referencia": {
    "tipoDoc": "01",
    "numero": "00100001010000000001",
    "fechaEmision": "2026-04-15",
    "codigo": "01",
    "razon": "Anulación de factura por error en datos"
  },
  "lineas": [
    {
      "codigo": "8422200000000",
      "detalle": "Servicio de Internet 100Mbps",
      "cantidad": 1,
      "precioUnitario": 50.00,
      "impuesto": { "codigo": "01", "codigoTarifa": "08", "tarifa": 13 }
    }
  ]
}`} />
        <div className="p-2 bg-gray-100 rounded text-xs mt-2">
          <strong>referencia.codigo:</strong> 01 = Anula documento, 02 = Corrige texto, 03 = Corrige monto, 05 = Sustituye comprobante, 99 = Otros
        </div>
      </Section>

      {/* 4. Referencia de Endpoints */}
      <Section title="4. Referencia de Endpoints">
        <h4 className="font-semibold text-gray-700 mb-2">Clientes</h4>
        <Endpoint method="GET" path="/receptores?q=EJEMPLO" description="Buscar clientes por nombre o cédula">
          <p className="text-sm text-gray-600">Parámetros: <code>q</code> (búsqueda), <code>page</code>, <code>limit</code></p>
        </Endpoint>
        <Endpoint method="GET" path="/receptores/buscar/:identificacion" description="Buscar cliente exacto por cédula">
          <p className="text-sm text-gray-600">Retorna el cliente si existe, 404 si no.</p>
        </Endpoint>
        <Endpoint method="GET" path="/receptores/:id" description="Obtener cliente con exoneraciones">
          <p className="text-sm text-gray-600">Incluye array <code>exoneraciones</code> con CABYS autorizados.</p>
        </Endpoint>
        <Endpoint method="POST" path="/receptores" description="Crear o actualizar cliente">
          <p className="text-sm text-gray-600">Si la identificación ya existe, actualiza. Si no, crea nuevo.</p>
        </Endpoint>

        <h4 className="font-semibold text-gray-700 mb-2 mt-4">Exoneraciones</h4>
        <Endpoint method="GET" path="/receptores/:id/exoneraciones" description="Listar exoneraciones del cliente">
          <p className="text-sm text-gray-600">Retorna todas las exoneraciones registradas.</p>
        </Endpoint>
        <Endpoint method="POST" path="/receptores/:id/exoneraciones" description="Registrar exoneración (consulta Hacienda)">
          <p className="text-sm text-gray-600">Solo envíe <code>{`{ "numeroDocumento": "AL-XXXXX-XX" }`}</code>. El sistema consulta Hacienda automáticamente.</p>
        </Endpoint>

        <h4 className="font-semibold text-gray-700 mb-2 mt-4">Productos</h4>
        <Endpoint method="GET" path="/productos?q=internet" description="Buscar productos">
          <p className="text-sm text-gray-600">Busca en el catálogo de productos por nombre, código o CABYS.</p>
        </Endpoint>
        <Endpoint method="GET" path="/productos/:id" description="Obtener producto por ID" />
        <Endpoint method="POST" path="/productos" description="Crear producto">
          <CodeBlock code={`{ "codigo": "SRV-001", "nombre": "Internet 100Mbps", "precioUnitario": 50, "codigoCabys": "8422200000000", "impuestoTarifa": 13, "moneda": "USD" }`} />
        </Endpoint>
        <Endpoint method="PUT" path="/productos/:id" description="Actualizar producto" />
        <Endpoint method="DELETE" path="/productos/:id" description="Eliminar producto" />

        <h4 className="font-semibold text-gray-700 mb-2 mt-4">Documentos (Facturas)</h4>
        <Endpoint method="POST" path="/documents" description="Crear FE, NC, ND, TE" />
        <Endpoint method="GET" path="/documents" description="Listar documentos con filtros">
          <p className="text-sm text-gray-600">Filtros: <code>status</code>, <code>tipo</code>, <code>desde</code>, <code>hasta</code>, <code>search</code>, <code>page</code>, <code>limit</code></p>
        </Endpoint>
        <Endpoint method="GET" path="/documents/:id" description="Obtener documento completo" />
        <Endpoint method="GET" path="/documents/:id/status" description="Estado en Hacienda (para polling)" />
        <Endpoint method="GET" path="/documents/:id/pdf" description="Descargar PDF" />
        <Endpoint method="GET" path="/documents/:id/xml" description="Descargar XML firmado" />
        <Endpoint method="POST" path="/documents/:id/submit" description="Reenviar a Hacienda" />

        <h4 className="font-semibold text-gray-700 mb-2 mt-4">Respaldos (permiso respaldos.read)</h4>
        <Endpoint method="GET" path="/respaldos" description="Historial de respaldos de la cuenta: alcance (cuenta o empresa), estado, tamaño, fechas y restauraciones. Filtros: emisor, limit" />
        <Endpoint method="GET" path="/respaldos/config" description="Programación (frecuencia, hora, retención, próxima) y destino (Kuanta o propio)" />

        <h4 className="font-semibold text-gray-700 mb-2 mt-4">Utilidades</h4>
        <Endpoint method="GET" path="/status" description="Health check" />
        <Endpoint method="GET" path="/tipo-cambio" description="Tipo de cambio BCCR (compra/venta)" />
        <Endpoint method="GET" path="/cabys/search?q=internet" description="Buscar códigos CABYS" />
      </Section>

      {/* 5. Tablas de Referencia */}
      <Section title="5. Tablas de Referencia">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-semibold text-sm">Tipo de Documento</div>
            <table className="w-full text-xs"><tbody className="divide-y">
              <tr><td className="px-3 py-1.5 font-mono">01</td><td className="px-3 py-1.5">Factura Electrónica (FE)</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">02</td><td className="px-3 py-1.5">Nota de Débito (ND)</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">03</td><td className="px-3 py-1.5">Nota de Crédito (NC)</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">04</td><td className="px-3 py-1.5">Tiquete Electrónico (TE)</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">09</td><td className="px-3 py-1.5">Factura Exportación (FEE)</td></tr>
            </tbody></table>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-semibold text-sm">Condición de Venta</div>
            <table className="w-full text-xs"><tbody className="divide-y">
              <tr><td className="px-3 py-1.5 font-mono">01</td><td className="px-3 py-1.5">Contado</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">02</td><td className="px-3 py-1.5">Crédito</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">03</td><td className="px-3 py-1.5">Consignación</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">04</td><td className="px-3 py-1.5">Apartado</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">99</td><td className="px-3 py-1.5">Otros</td></tr>
            </tbody></table>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-semibold text-sm">Medio de Pago</div>
            <table className="w-full text-xs"><tbody className="divide-y">
              <tr><td className="px-3 py-1.5 font-mono">01</td><td className="px-3 py-1.5">Efectivo</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">02</td><td className="px-3 py-1.5">Tarjeta</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">03</td><td className="px-3 py-1.5">Cheque</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">04</td><td className="px-3 py-1.5">Transferencia / depósito</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">99</td><td className="px-3 py-1.5">Otros</td></tr>
            </tbody></table>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-semibold text-sm">Tipo Identificación</div>
            <table className="w-full text-xs"><tbody className="divide-y">
              <tr><td className="px-3 py-1.5 font-mono">01</td><td className="px-3 py-1.5">Cédula Física</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">02</td><td className="px-3 py-1.5">Cédula Jurídica</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">03</td><td className="px-3 py-1.5">DIMEX</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">04</td><td className="px-3 py-1.5">NITE</td></tr>
            </tbody></table>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-semibold text-sm">IVA — Código Tarifa</div>
            <table className="w-full text-xs"><tbody className="divide-y">
              <tr><td className="px-3 py-1.5 font-mono">01</td><td className="px-3 py-1.5">0% Exento</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">02</td><td className="px-3 py-1.5">1% Tarifa reducida</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">03</td><td className="px-3 py-1.5">2% Tarifa reducida</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">04</td><td className="px-3 py-1.5">4% Tarifa reducida</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">08</td><td className="px-3 py-1.5">13% Tarifa general</td></tr>
            </tbody></table>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-semibold text-sm">Estado Hacienda</div>
            <table className="w-full text-xs"><tbody className="divide-y">
              <tr><td className="px-3 py-1.5 font-mono">borrador</td><td className="px-3 py-1.5">No enviado aún</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">procesando</td><td className="px-3 py-1.5">Enviado, esperando respuesta</td></tr>
              <tr><td className="px-3 py-1.5 font-mono text-green-700 font-bold">aceptado</td><td className="px-3 py-1.5">Aceptado por Hacienda</td></tr>
              <tr><td className="px-3 py-1.5 font-mono text-red-700 font-bold">rechazado</td><td className="px-3 py-1.5">Rechazado por Hacienda</td></tr>
              <tr><td className="px-3 py-1.5 font-mono">error</td><td className="px-3 py-1.5">Error de comunicación</td></tr>
            </tbody></table>
          </div>
        </div>
      </Section>

      {/* 6. Errores */}
      <Section title="6. Códigos de Error HTTP">
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><th className="px-4 py-2 text-left">HTTP</th><th className="px-4 py-2 text-left">Significado</th><th className="px-4 py-2 text-left">Qué hacer</th></tr></thead>
            <tbody className="divide-y text-xs">
              <tr><td className="px-4 py-2 font-mono font-bold">200</td><td className="px-4 py-2">Éxito</td><td className="px-4 py-2 text-gray-500">Operación completada</td></tr>
              <tr><td className="px-4 py-2 font-mono font-bold">201</td><td className="px-4 py-2">Creado</td><td className="px-4 py-2 text-gray-500">Documento/recurso creado</td></tr>
              <tr><td className="px-4 py-2 font-mono font-bold text-yellow-600">400</td><td className="px-4 py-2">Datos inválidos</td><td className="px-4 py-2 text-gray-500">Revise los campos enviados</td></tr>
              <tr><td className="px-4 py-2 font-mono font-bold text-red-600">401</td><td className="px-4 py-2">No autorizado</td><td className="px-4 py-2 text-gray-500">Verifique x-api-key, x-signature y x-timestamp</td></tr>
              <tr><td className="px-4 py-2 font-mono font-bold text-red-600">403</td><td className="px-4 py-2">Sin permisos</td><td className="px-4 py-2 text-gray-500">La credencial no tiene permiso para esta acción</td></tr>
              <tr><td className="px-4 py-2 font-mono font-bold">404</td><td className="px-4 py-2">No encontrado</td><td className="px-4 py-2 text-gray-500">El recurso no existe</td></tr>
              <tr><td className="px-4 py-2 font-mono font-bold text-orange-600">429</td><td className="px-4 py-2">Rate limit</td><td className="px-4 py-2 text-gray-500">Espere antes de reintentar (máx 100 req/min)</td></tr>
              <tr><td className="px-4 py-2 font-mono font-bold text-red-600">500</td><td className="px-4 py-2">Error interno</td><td className="px-4 py-2 text-gray-500">Contacte soporte</td></tr>
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
